import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useEditHouseSchema } from "@features/EditHouseModal/useEditHouseSchema";
import { Button } from "@components/Button/Button";
import { ExpandingContainer } from "@components/ExpandingContainer/ExpandingContainer";
import { HouseIcon } from "@components/icons/common";
import { FormAddressInput } from "@components/inputs/form/FormAddressInput/FormAddressInput";
import { FormHouseDetailsInput } from "@components/inputs/form/FormHouseDetailsInput/FormHouseDetailsInput";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useHouseName } from "@hooks/useHouseName";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useEditJoinBillServiceMutation } from "@services/jointBillServices/mutations";
import { useJointBillServiceEditDataQuery } from "@services/jointBillServices/queries";
import {
  type HeatingTypeId,
  type JointBillServiceTypeId,
} from "@services/jointBillServices/types";
import { useOverviewQuery } from "@services/overview/queries";
import { isNullOrUndefined } from "@utils/common";
import {
  formatDecimalNumberInputValue,
  parseDecimalNumberInputValue,
} from "@utils/units";
import { type IAddressFormData } from "@/types";

interface IFormData extends IAddressFormData {
  sortOrderAndDisplayName: {
    orderNumber: number;
    displayName: string;
  };
  jointBillServiceInformation: {
    firstName: string;
    lastName: string;
    jointBillServiceTypeId?: JointBillServiceTypeId;
    area?: string;
    heatingTypeId?: HeatingTypeId;
  };
}

interface IEditHouseModalProps extends IUseModalProps {
  jointBillServiceId: number;
}

export const EditHouseModal: React.FC<IEditHouseModalProps> = (props) => {
  const { jointBillServiceId, ...modalProps } = props;
  const modal = useModal(modalProps);
  const { t } = useTranslation();
  const { isHouseAddressOptional } = useFeatureConfig();

  const { data: overview } = useOverviewQuery();
  const { data: houseData } =
    useJointBillServiceEditDataQuery(jointBillServiceId);

  const [addressDirty, setAddressDirty] = useState(false);

  const isOwner =
    overview?.jointBillServices.find(
      (jbs) => jbs.jointBillServiceId === jointBillServiceId,
    )?.isOwner ?? false;

  const sortOrderOptions =
    overview?.jointBillServices.map((_, index) => ({
      value: index + 1,
      label: index + 1,
    })) ?? [];

  const schema = useEditHouseSchema(isHouseAddressOptional, addressDirty);

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    resetField,
    setFocus,
    formState,
  } = useForm<IFormData>({
    defaultValues: {
      municipalityId: houseData?.address.municipalityId,
      settlementId: houseData?.address.settlementId,
      streetId: houseData?.address.streetId,
      house: houseData?.address.houseNumber,
      apartment: houseData?.address.apartmentNumber,
      sortOrderAndDisplayName: {
        orderNumber:
          houseData?.sortOrder ??
          (overview
            ? overview.jointBillServices.findIndex(
                (jbs) => jbs.jointBillServiceId === jointBillServiceId,
              ) + 1
            : undefined),
        displayName: houseData?.displayName,
      },
      jointBillServiceInformation: {
        firstName: houseData?.firstName,
        lastName: houseData?.lastName,
        jointBillServiceTypeId: houseData?.jointBillServiceTypeId,
        heatingTypeId: houseData?.heatingTypeId,
        ...(!isNullOrUndefined(houseData?.area)
          ? { area: formatDecimalNumberInputValue(houseData.area) }
          : {}),
      },
    },
    resolver: zodResolver(schema),
  });

  const [municipalityId, settlementId, streetId, house, apartment] = useWatch({
    name: ["municipalityId", "settlementId", "streetId", "house", "apartment"],
    control,
  });

  useEffect(() => {
    const houseDirty = Object.keys(formState.dirtyFields).some((key) =>
      [
        "municipalityId",
        "settlementId",
        "streetId",
        "house",
        "apartment",
      ].includes(key),
    );
    if (houseDirty) {
      setAddressDirty(true);
    } else {
      setAddressDirty(false);
    }
  }, [formState.dirtyFields, formState.dirtyFields.municipalityId]);

  const houseName = useHouseName(
    municipalityId,
    settlementId,
    streetId,
    house,
    apartment,
  );

  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync: editJointBillService, isPending } =
    useEditJoinBillServiceMutation(jointBillServiceId);

  const editHouse: SubmitHandler<IFormData> = async (data) => {
    const address =
      (isHouseAddressOptional && addressDirty) || !isHouseAddressOptional
        ? {
            municipalityId: data.municipalityId ?? 0,
            settlementId: data.settlementId ?? 0,
            streetId: data.streetId ?? 0,
            houseNumber: data.house ?? "",
            apartmentNumber: data.apartment ?? "",
          }
        : undefined;

    const response = await editJointBillService({
      address: address,
      sortOrderAndDisplayName: {
        ...(data.sortOrderAndDisplayName.displayName
          ? { displayName: data.sortOrderAndDisplayName.displayName }
          : {}),
        ...(data.sortOrderAndDisplayName.orderNumber
          ? { orderNumber: data.sortOrderAndDisplayName.orderNumber }
          : {}),
      },
      jointBillServiceInformation: {
        firstName: data.jointBillServiceInformation.firstName,
        lastName: data.jointBillServiceInformation.lastName,
        ...(data.jointBillServiceInformation.jointBillServiceTypeId
          ? {
              jointBillServiceTypeId:
                data.jointBillServiceInformation.jointBillServiceTypeId,
            }
          : {}),
        ...(!isNullOrUndefined(data.jointBillServiceInformation.area)
          ? {
              area: parseDecimalNumberInputValue(
                data.jointBillServiceInformation.area,
              ),
            }
          : {}),
        ...(data.jointBillServiceInformation.heatingTypeId
          ? { heatingTypeId: data.jointBillServiceInformation.heatingTypeId }
          : {}),
      },
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      toast.success(t("editHouseModal.toast.success"));
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: HouseIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("editHouseModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(editHouse)}
            className="w-full"
            isLoading={isPending}
          >
            {t("editHouseModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("editHouseModal.title")}
      subtitle={houseName || t("common.defaultAddress")}
      className="md:max-w-xl"
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-3">
            <FormInput
              name="sortOrderAndDisplayName.displayName"
              placeholder={houseName || t("common.defaultAddress")}
              control={control}
              label={t("editHouseModal.form.fields.displayName.label")}
            />
          </div>
          <FormSelect
            name="sortOrderAndDisplayName.orderNumber"
            control={control}
            options={sortOrderOptions}
            label={t("editHouseModal.form.fields.sortOrder.label")}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <FormInput
            name="jointBillServiceInformation.firstName"
            control={control}
            label={t("editHouseModal.form.fields.firstName.label")}
            disabled={!isOwner}
          />
          <FormInput
            name="jointBillServiceInformation.lastName"
            control={control}
            label={t("editHouseModal.form.fields.lastName.label")}
            disabled={!isOwner}
          />
        </div>
        <ExpandingContainer
          buttonLabel={t("editHouseModal.completeInformationButton")}
          isEnabled={isHouseAddressOptional}
          defaultOpen
          className="pt-4"
        >
          <FormAddressInput
            municipalityFieldName="municipalityId"
            settlementFieldName="settlementId"
            streetFieldName="streetId"
            houseFieldName="house"
            apartmentFieldName="apartment"
            control={control}
            resetField={resetField}
            setFocus={setFocus}
            setValue={setValue}
            disabled={!houseData?.addressIsEditable}
          />
          <FormHouseDetailsInput
            jointBillServiceTypeIdFieldName="jointBillServiceInformation.jointBillServiceTypeId"
            areaFieldName="jointBillServiceInformation.area"
            heatingTypeIdFieldName="jointBillServiceInformation.heatingTypeId"
            setValue={setValue}
            control={control}
            disabled={!isOwner}
          />
        </ExpandingContainer>
      </div>
    </Modal>
  );
};
